import { Checkbox, ListSubheader, MenuItem, Select } from '@mui/material';
import { Label } from '@trucktrax/trucktrax-common';
import cx from 'classnames';
import React from 'react';

import styles from '../admin/AdminAddModal.module.css';
import ownStyles from './GroupedMultiSelect.module.css';

/**
 * A multi-select dropdown that can group its options under headers and offer a
 * select-all toggle.
 *
 * trucktrax-common's MultiSelectDropDownList does neither: its items are a flat
 * list and there is no all/none affordance. Email Notifications needs both --
 * plants and checklists are chosen across every region an admin can reach, so
 * they only make sense under region headers, and picking every plant is a
 * routine gesture that should not be twenty clicks. Rather than fork the shared
 * component (it ships from ui/trucktrax-web-common on its own release cadence,
 * and every existing caller is happy with the flat behaviour), this is a local
 * control built on the same MUI Select, matching the grouped-dropdown idiom
 * CardSelect already uses for its single-select case.
 *
 * Selection is carried as an array of option keys rather than option objects: a flat
 * string array is what the table's header filters want to compare against. Its only
 * callers are those filters -- the add modal used it until notifications moved onto a
 * per-plant checkbox list.
 */

export interface GroupedMultiSelectOption {
  /** Identity, and what the selection is expressed in. */
  key: string;
  label: string;
  /** Header this option sits under. Options with no group render before the first header. */
  group?: string;
  disabled?: boolean;
}

export interface GroupedMultiSelectProps {
  items: GroupedMultiSelectOption[];
  /** Selected option keys. */
  value?: string[];
  onChange: (selectedKeys: string[]) => void;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  className?: string;
  dataTest?: string;
  /** Adds the all/none row at the top of the menu. */
  showSelectAll?: boolean;
}

/**
 * Menu value for the select-all row. Prefixed so it cannot collide with a plant
 * url or a checklist id.
 */
export const SELECT_ALL_KEY = '__grouped-multi-select-all__';

export const SELECT_ALL_TEXT = 'Select All';
export const UNSELECT_ALL_TEXT = 'Unselect All';

const UNGROUPED = '__ungrouped__';

/**
 * The dropdown chevron, matching MultiSelectDropDownList's. At module scope so it is
 * one component type rather than a new one per render.
 */
const DropdownIcon = () => (
  <i aria-hidden="true" className={cx('icon-chevron-left', ownStyles.iconRight)} />
);

/** The options a select-all may touch. A locked option keeps whatever state it has. */
const selectableKeys = (items: GroupedMultiSelectOption[]) =>
  items.filter(item => !item.disabled).map(item => item.key);

/**
 * Groups in the order their first option appears, so the caller controls ordering
 * by sorting `items` rather than by fighting an alphabetical grouping here.
 */
export function groupOptions(
  items: GroupedMultiSelectOption[],
): { group: string; options: GroupedMultiSelectOption[] }[] {
  const order: string[] = [];
  const byGroup = new Map<string, GroupedMultiSelectOption[]>();

  items.forEach(item => {
    const group = item.group ?? UNGROUPED;
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      order.push(group);
    }
    byGroup.get(group)!.push(item);
  });

  return order.map(group => ({ group, options: byGroup.get(group)! }));
}

export default function GroupedMultiSelect(props: GroupedMultiSelectProps) {
  const {
    items,
    value = [],
    onChange,
    label,
    isRequired = false,
    disabled = false,
    placeholder = '',
    errorMessage,
    className,
    dataTest,
    showSelectAll = false,
  } = props;

  const selectable = selectableKeys(items);
  // Every option an admin could pick is picked. An empty list is not "all".
  const allSelected = selectable.length > 0 && selectable.every(key => value.includes(key));

  const labelFor = (key: string) => items.find(item => item.key === key)?.label;

  const toggleAll = () => {
    // Locked options are left exactly as they are, in both directions.
    const locked = value.filter(key => !selectable.includes(key));
    onChange(allSelected ? locked : [...locked, ...selectable]);
  };

  const handleChange = (selection: string[]) => {
    if (selection.includes(SELECT_ALL_KEY)) {
      toggleAll();
      return;
    }
    // MUI hands back the menu's own order; keep the caller's option order instead
    // so a saved selection does not reshuffle by the sequence it was clicked in.
    const chosen = new Set(selection);
    onChange(items.filter(item => chosen.has(item.key)).map(item => item.key));
  };

  /**
   * Selected labels, in option order. Keys with no matching option -- a plant that
   * has since been archived, say -- are dropped rather than rendered blank.
   */
  const renderValue = (selection: string[]) => {
    const labels = selection.map(labelFor).filter((text): text is string => !!text);
    if (labels.length === 0) {
      return <span className={ownStyles.placeholder}>{placeholder}</span>;
    }
    return labels.join(', ');
  };

  const groups = groupOptions(items);

  const renderOption = (option: GroupedMultiSelectOption) => (
    <MenuItem
      key={`option-${option.key}`}
      value={option.key}
      disabled={option.disabled}
      className={cx(styles.groupItem, ownStyles.menuItem)}
      data-test={dataTest ? `${dataTest}-option` : undefined}
    >
      <Checkbox
        color="primary"
        checked={value.includes(option.key)}
        disabled={option.disabled}
        className={ownStyles.checkbox}
      />
      <span className={ownStyles.optionLabel}>{option.label}</span>
    </MenuItem>
  );

  return (
    <div className={className}>
      {label ? (
        <Label isRequired={isRequired} className={disabled ? styles.labelDisabled : ''}>
          {label}
        </Label>
      ) : null}
      <div
        className={cx(styles.dropDownListDiv, errorMessage && styles.hasError)}
        data-test={dataTest}
      >
        <Select
          multiple
          displayEmpty
          disabled={disabled}
          value={value}
          onChange={event => handleChange(event.target.value as string[])}
          className={ownStyles.selectContainer}
          inputProps={{ className: ownStyles.select }}
          renderValue={selection => renderValue(selection as string[])}
          IconComponent={DropdownIcon}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            style: { maxHeight: '450px', zIndex: 1501 },
          }}
        >
          {/*
            Select is not a real listbox here: it maps children to values, so the
            all/none row has to be a MenuItem carrying a sentinel, intercepted in
            handleChange. Rendered flat rather than inside the group loop because
            it spans every group.
          */}
          {showSelectAll
            ? [
                <MenuItem
                  key="select-all"
                  value={SELECT_ALL_KEY}
                  className={cx(styles.groupItem, ownStyles.menuItem, ownStyles.selectAllItem)}
                  data-test={dataTest ? `${dataTest}-select-all` : undefined}
                >
                  <Checkbox color="primary" checked={allSelected} className={ownStyles.checkbox} />
                  <span className={ownStyles.optionLabel}>
                    {allSelected ? UNSELECT_ALL_TEXT : SELECT_ALL_TEXT}
                  </span>
                </MenuItem>,
              ]
            : []}
          {/*
            Flattened deliberately: MUI reads Select's children to map values, and a
            <React.Fragment> wrapper per group hides the MenuItems from that pass.
          */}
          {groups.flatMap(({ group, options }) =>
            group === UNGROUPED
              ? options.map(renderOption)
              : [
                  <ListSubheader
                    key={`group-${group}`}
                    className={cx(styles.groupHeader, ownStyles.groupHeader)}
                    disableSticky
                  >
                    {group}
                  </ListSubheader>,
                  ...options.map(renderOption),
                ],
          )}
        </Select>
      </div>
      {errorMessage ? (
        <div className={styles.multiSelectErrorMessage} data-test={`${dataTest}-error`}>
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}
